/*

✅ Modern 2026 UI modal (colorful + premium SaaS style)
✅ No backend or API changes
✅ No logic changes
✅ Only UX upgrade (clean + professional)
✅ Fully integrated styles included

*/

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Form, Alert, Spinner, Modal } from "react-bootstrap";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(
        "https://apiclientportal.myidealtd.co.uk/api/users/forgot-password",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await res.json();

if (!res.ok) {
  setError(data.message || "Request failed.");
} else {
  setSubmittedEmail(email); // Store email for modal
  setShowModal(true);       // Open modal
  setEmail("");             // Clear form input
}
    } catch (err) {
      setError("Server error. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     2026 MODERN STYLES
  ========================= */

  const page = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(circle at top left, #5b8cff33, transparent 40%)," +
      "radial-gradient(circle at bottom right, #7c5cff33, transparent 40%)," +
      "#050816",
    fontFamily: "Inter, system-ui, sans-serif",
    padding: "20px",
  };

  const card = {
    width: "100%",
    maxWidth: "420px",
    padding: "40px",
    borderRadius: "24px",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
    color: "white",
    backdropFilter: "blur(20px)",
  };

  const title = {
    fontSize: "2.2rem",
    fontWeight: "800",
    marginBottom: "10px",
  };

  const subtitle = {
    color: "rgba(255,255,255,0.65)",
    marginBottom: "25px",
    fontSize: "14px",
  };

  const input = {
    width: "100%",
    height: "52px",
    borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.05)",
    color: "white",
    padding: "0 15px",
    outline: "none",
    marginBottom: "20px",
  };

  const button = {
    width: "100%",
    height: "52px",
    borderRadius: "14px",
    border: "none",
    cursor: "pointer",
    fontWeight: "700",
    color: "white",
    background: "linear-gradient(135deg, #6a11cb, #2575fc, #00c6ff)",
    transition: "0.3s",
  };

  /* =========================
     MODAL STYLES (2026 SaaS)
  ========================= */

  const modalWrapper = {
    borderRadius: "20px",
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 35px 100px rgba(0,0,0,0.3)",
  };

  const modalHeader = {
    background: "linear-gradient(135deg, #6a11cb, #2575fc, #00c6ff)",
    color: "white",
    textAlign: "center",
    padding: "26px 20px",
  };

  const iconCircle = {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    margin: "0 auto",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    fontWeight: "bold",
    backdropFilter: "blur(10px)",
  };

  const modalBody = {
    padding: "24px",
    textAlign: "center",
  };

  const emailBox = {
    padding: "10px 14px",
    borderRadius: "10px",
    background: "#f4f7ff",
    fontWeight: 600,
    fontSize: "14px",
    color: "#1a1a1a",
    display: "inline-block",
  };

  const infoCard = {
    marginTop: "16px",
    padding: "12px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #fdfbff, #eef5ff)",
    fontSize: "13px",
    color: "#444",
    border: "1px solid #e6ecff",
  };

  const modalFooter = {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    padding: "16px",
    borderTop: "1px solid #eee",
  };

  const primaryBtn = {
    flex: 1,
    textAlign: "center",
    padding: "10px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #6a11cb, #2575fc)",
    color: "white",
    textDecoration: "none",
    fontWeight: "600",
  };

  const secondaryBtn = {
    flex: 1,
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
  };

  return (
    <div style={page}>
      <div style={card}>
        <h2 style={title}>Forgot Password</h2>

        <p style={subtitle}>
          Enter your email and we’ll send a secure reset link.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <input
            style={input}
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <button type="submit" style={button} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> Sending...
              </>
            ) : (
              "Send Reset Link"
            )}
          </button>
        </Form>

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <Link to="/login" style={{ color: "#8ab4ff", textDecoration: "none" }}>
            Back to Login
          </Link>
        </div>
      </div>

      {/* =========================
          MODERN 2026 MODAL
      ========================= */}


    <Modal
  show={showModal}
  onHide={() => setShowModal(false)}
  centered
  backdrop="static"
  contentClassName="border-0 bg-transparent"
>
  <div
    style={{
      borderRadius: "28px",
      overflow: "hidden",
      background:
        "linear-gradient(180deg, rgba(10,18,40,0.98) 0%, rgba(6,12,28,0.98) 100%)",
      backdropFilter: "blur(40px)",
      border: "1px solid rgba(255,255,255,0.08)",
      boxShadow:
        "0 40px 120px rgba(0,0,0,0.65), 0 0 60px rgba(91,140,255,0.18)",
    }}
  >
    {/* Header */}
    <div
      style={{
        padding: "40px 30px",
        textAlign: "center",
        background:
          "linear-gradient(135deg, rgba(91,140,255,0.18), rgba(124,92,255,0.18))",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div
        style={{
          width: "88px",
          height: "88px",
          borderRadius: "50%",
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(135deg,#5b8cff,#7c5cff)",
          color: "#fff",
          fontSize: "38px",
          fontWeight: "700",
          boxShadow:
            "0 15px 40px rgba(91,140,255,0.45)",
        }}
      >
        ✓
      </div>

      <h3
        style={{
          marginTop: "20px",
          marginBottom: "8px",
          color: "#fff",
          fontWeight: "800",
          fontSize: "1.8rem",
        }}
      >
        Check Your Email
      </h3>

      <p
        style={{
          margin: 0,
          color: "rgba(255,255,255,0.75)",
          fontSize: "14px",
        }}
      >
        Password reset request received
      </p>
    </div>

    {/* Body */}
    <div
      style={{
        padding: "32px",
      }}
    >
      <p
        style={{
          textAlign: "center",
          color: "rgba(255,255,255,0.88)",
          fontSize: "15px",
          lineHeight: "1.8",
          marginBottom: "22px",
        }}
      >
        If an account is associated with the email address below,
        password reset instructions will be sent shortly.
      </p>

      {/* Email Card */}
      <div
        style={{
          padding: "18px 22px",
          borderRadius: "18px",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
          border: "1px solid rgba(255,255,255,0.08)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            textTransform: "uppercase",
            letterSpacing: "2px",
            color: "rgba(255,255,255,0.45)",
            marginBottom: "10px",
          }}
        >
          Email Address
        </div>

        <div
          style={{
            color: "#ffffff",
            fontWeight: "600",
            fontSize: "15px",
            wordBreak: "break-word",
          }}
        >
          {submittedEmail}
        </div>
      </div>

      {/* Security Card */}
      <div
        style={{
          marginTop: "24px",
          padding: "20px",
          borderRadius: "18px",
          background:
            "linear-gradient(135deg, rgba(91,140,255,0.08), rgba(124,92,255,0.08))",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div
          style={{
            color: "#ffffff",
            fontWeight: "700",
            marginBottom: "12px",
            fontSize: "14px",
          }}
        >
          Security Information
        </div>

        <div
          style={{
            color: "rgba(255,255,255,0.72)",
            fontSize: "13px",
            lineHeight: "1.9",
          }}
        >
          • Password reset links expire after <strong>15 minutes</strong>
          <br />
          • Each reset link can only be used once
          <br />
          • Check your Spam or Promotions folder if the email does not arrive
          <br />
          • Delivery may take several minutes depending on your email provider
        </div>
      </div>

      <div
        style={{
          marginTop: "20px",
          textAlign: "center",
          color: "rgba(255,255,255,0.45)",
          fontSize: "12px",
          lineHeight: "1.7",
        }}
      >
        For security reasons, we cannot confirm whether an account exists for the email address provided.
      </div>
    </div>

    {/* Footer */}
    <div
      style={{
        display: "flex",
        gap: "12px",
        padding: "24px 32px",
        borderTop: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <button
        onClick={() => setShowModal(false)}
        style={{
          flex: 1,
          height: "54px",
          borderRadius: "14px",
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.05)",
          color: "#fff",
          fontWeight: "600",
          cursor: "pointer",
        }}
      >
        Close
      </button>

      <Link
        to="/login"
        style={{
          flex: 1,
          height: "54px",
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textDecoration: "none",
          color: "#fff",
          fontWeight: "700",
          background:
            "linear-gradient(135deg,#5b8cff,#7c5cff)",
          boxShadow:
            "0 12px 35px rgba(91,140,255,0.35)",
        }}
      >
        Return to Sign In
      </Link>
    </div>
  </div>
</Modal>


    </div>
  );
}

export default ForgotPassword;